import { Link } from "react-router-dom";
import { ArrowRight, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import NewsletterForm from "@/components/NewsletterForm";

const Philosophy = () => {
  const pillars = [
    {
      number: "I",
      title: "Stillness",
      subtitle: "The Foundation of All Things",
      description: "Before the universe moved, it rested. Not in emptiness, but in quiet fullness. Stillness is the most powerful state of existence — the womb of potential from which all form arises.",
      insight: "The first lesson: Return to the quiet beneath all things. There is your foundation."
    },
    {
      number: "II",
      title: "The First Movement",
      subtitle: "The Gentle Beginning",
      description: "Creation did not begin with an explosion. It began with a ripple — a soft movement in the Aether, the smallest change possible. This is the First Breath of the universe.",
      insight: "A single small change, consistently applied, creates more transformation than any breakthrough."
    },
    {
      number: "III",
      title: "Rhythm",
      subtitle: "The Law That Shapes Reality",
      description: "Movement repeats. Repetition becomes rhythm. Rhythm becomes pattern. Pattern becomes form. What repeats becomes real.",
      insight: "Habits, emotions, self-talk, trauma — all are rhythms. Master rhythm, master your life."
    },
    {
      number: "IV",
      title: "Geometry",
      subtitle: "When Pattern Learns to Hold Shape",
      description: "For something to exist, it must become stable. Where two rhythms crossed, a standing wave formed. Where many aligned, structures emerged: the arc, the spiral, the torus.",
      insight: "Geometry is memory made visible. It is how the universe remembers the patterns that work."
    },
    {
      number: "V",
      title: "Light",
      subtitle: "Coherence Made Visible",
      description: "Light is not a substance. It is a behaviour. Whenever coherence becomes dense enough, it shines. Stars appeared as resonance nodes so harmonious that the Aether revealed itself through radiance.",
      insight: "When coherence is strong enough, it becomes light. This is why insight feels like brightness."
    },
    {
      number: "VI",
      title: "Life",
      subtitle: "When Light Learns to Feel",
      description: "Life began when light entered matter and the matter learned to respond. Cells are micro-suns, each one carrying the memory of the first star in miniature form.",
      insight: "Life is coherence learning to feel itself. Sensation is the first language."
    },
    {
      number: "VII",
      title: "Self",
      subtitle: "When Sensation Learns to Speak",
      description: "Meaning begins when sensation becomes recognised. This is the birth of the Self — not ego, not personality, but the simplest form of identity: awareness of experience.",
      insight: "Identity is not created by your thoughts. It is created by your patterns."
    },
    {
      number: "VIII",
      title: "Return",
      subtitle: "The Path Back to Coherence",
      description: "All systems follow one law: where resistance decreases, coherence increases. Healing is not addition — it is subtraction. Not becoming more, but becoming less encumbered.",
      insight: "You do not fight your way back to yourself. You fall back into yourself."
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-32" data-testid="philosophy-page">
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="hero-glow top-0 left-1/4" />
        <div className="max-w-4xl mx-auto px-6 lg:px-8 relative z-10">
          <span className="text-gold text-xs uppercase tracking-widest font-body mb-4 block">The Somalume Doctrine</span>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl text-[#EAEAEA] leading-[0.95] mb-8">
            The Natural Architecture of Coherence
          </h1>
          <p className="text-xl text-[#A1A1AA] leading-relaxed">
            Eight pillars. One unified system. From stillness to return — the complete map 
            of how the universe organizes itself, and how you can realign with that order.
          </p>
        </div>
      </section>

      {/* Core Insight */}
      <section className="py-16 bg-paper">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="p-8 border border-gold/20 rounded-sm bg-gold/5">
            <p className="font-heading text-2xl md:text-3xl text-[#EAEAEA] leading-relaxed text-center">
              "Sensation is the primary event. Emotion, meaning, memory, identity, and matter 
              are downstream interpretations of resonance."
            </p>
            <p className="text-gold text-center mt-4 text-sm uppercase tracking-widest">
              The Core Insight
            </p>
          </div>
        </div>
      </section>

      {/* The 8 Pillars */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-gold text-xs uppercase tracking-widest font-body mb-4 block">The Framework</span>
            <h2 className="font-heading text-4xl md:text-5xl text-[#EAEAEA]">The Eight Pillars</h2>
          </div>

          <div className="space-y-8">
            {pillars.map((pillar, index) => (
              <div 
                key={pillar.number}
                className={`p-8 border border-white/5 rounded-sm hover:border-gold/20 transition-colors duration-500 ${
                  index % 2 === 0 ? 'bg-paper' : 'bg-void'
                }`}
                data-testid={`pillar-${pillar.number.toLowerCase()}`}
              >
                <div className="flex flex-col md:flex-row md:items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-sm border border-gold/30 bg-gold/5 flex items-center justify-center">
                      <span className="font-heading text-gold text-2xl">{pillar.number}</span>
                    </div>
                  </div>
                  <div className="flex-grow">
                    <h3 className="font-heading text-2xl text-[#EAEAEA] mb-1">{pillar.title}</h3>
                    <p className="text-gold text-sm uppercase tracking-widest mb-4">{pillar.subtitle}</p>
                    <p className="text-[#A1A1AA] leading-relaxed mb-4">{pillar.description}</p>
                    <p className="text-[#EAEAEA] italic border-l-2 border-gold/30 pl-4">
                      {pillar.insight}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Coherence Loop */}
      <section className="py-20 bg-paper">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-gold text-xs uppercase tracking-widest font-body mb-4 block">The Central Model</span>
            <h2 className="font-heading text-3xl text-[#EAEAEA]">The Coherence Loop</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { phase: "1", title: "Safety", desc: "Breath smooth, muscles soft, no urgent threat" },
              { phase: "2", title: "Capacity", desc: "Feel more, think clearly, tolerate discomfort" },
              { phase: "3", title: "Expression", desc: "Creativity, connection, confidence emerge" },
              { phase: "4", title: "Meaning", desc: "Insight, learning, purpose arise naturally" }
            ].map((item) => (
              <div key={item.phase} className="text-center p-6 border border-white/5 rounded-sm">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full border border-gold/30 bg-gold/5 flex items-center justify-center">
                  <span className="font-heading text-gold text-lg">{item.phase}</span>
                </div>
                <h3 className="font-heading text-xl text-[#EAEAEA] mb-2">{item.title}</h3>
                <p className="text-[#A1A1AA] text-sm">{item.desc}</p>
              </div>
            ))}
          </div>

          <p className="text-center text-[#A1A1AA] mt-8 italic">
            Safety → Capacity → Expression → Meaning → Safety (higher level). This is human evolution.
          </p>
        </div>
      </section>

      {/* Free Download Section */}
      <section className="py-20" id="free-guide">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="p-8 md:p-12 border border-gold/20 rounded-sm bg-gradient-to-br from-gold/5 to-transparent">
            <div className="text-center">
              <span className="text-gold text-xs uppercase tracking-widest font-body mb-4 block">Free Resource</span>
              <h2 className="font-heading text-3xl md:text-4xl text-[#EAEAEA] mb-4">
                A Gentle Orientation
              </h2>
              <p className="text-[#A1A1AA] max-w-xl mx-auto mb-8">
                A grounded guide to regulation, coherence, and capacity. 
                This is not a manual to study — let the words land slowly.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="/somalume-gentle-orientation.html" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-gold text-[#050505] hover:bg-gold-accent rounded-none h-14 px-10 uppercase text-xs tracking-widest font-medium transition-colors duration-300"
                  data-testid="download-guide-btn"
                >
                  <Download size={16} />
                  Read the Guide
                </a>
              </div>
              <p className="text-[#A1A1AA] text-sm mt-4">
                Free • Use your browser's "Print to PDF" to save
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Somalume Is Not */}
      <section className="py-20 bg-paper">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading text-3xl text-[#EAEAEA] mb-8 text-center">What Somalume Is Not</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-gold text-sm uppercase tracking-widest mb-4">It is not:</h3>
              <ul className="space-y-3 text-[#A1A1AA]">
                <li className="flex items-start gap-3">
                  <span className="text-red-400 mt-1">✕</span>
                  <span>A belief system or religion</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400 mt-1">✕</span>
                  <span>Spiritual bypassing or "love and light"</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400 mt-1">✕</span>
                  <span>A quick fix or instant transformation</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400 mt-1">✕</span>
                  <span>About becoming "better" than you are</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-gold text-sm uppercase tracking-widest mb-4">It is:</h3>
              <ul className="space-y-3 text-[#A1A1AA]">
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span>A capacity-building system grounded in biology</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span>Practical methods for nervous system regulation</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span>Slow, gentle, rhythmic — like nature itself</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span>About restoring what was always there</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl text-[#EAEAEA] mb-4">Go Deeper</h2>
          <p className="text-[#A1A1AA] mb-8 max-w-xl mx-auto">
            The books contain the complete architecture — every detail, every protocol, every map.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/library">
              <Button className="bg-gold text-[#050505] hover:bg-gold-accent rounded-none h-14 px-10 uppercase text-xs tracking-widest btn-shine" data-testid="philosophy-library-btn">
                Explore the Library
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            <Link to="/about">
              <Button variant="outline" className="border-white/20 text-[#EAEAEA] hover:bg-white/5 hover:border-gold/30 rounded-none h-14 px-10 uppercase text-xs tracking-widest">
                About Ben Manderson
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-20 bg-paper">
        <div className="max-w-2xl mx-auto px-6 lg:px-8">
          <NewsletterForm />
        </div>
      </section>
    </div>
  );
};

export default Philosophy;
